import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { rolesApi, adminApi, quotaApi } from "@/services/api-v2.service";
import { Badge } from "@/components/ui/badge";
import { Trash2, Settings, Users } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import type { RoleQuota, UserQuota, RoleQuotaCreate, UserQuotaCreate, BulkRoleQuotaCreate, BulkUserQuotaCreate } from "@/types/api-v2";

const QUOTA_TYPES = [
  { value: 'objects', label: 'Объекты', description: 'Максимальное количество объектов' },
  { value: 'geometries', label: 'Геометрии', description: 'Максимальное количество геометрий' },
  { value: 'files', label: 'Файлы', description: 'Максимальное количество файлов' },
  { value: 'storage_mb', label: 'Хранилище (МБ)', description: 'Максимальный объем хранилища в МБ' }
];

export default function QuotaManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Состояние для форм
  const [newRoleQuota, setNewRoleQuota] = useState({
    roleId: '',
    quotaType: '',
    maxValue: '',
    description: ''
  });

  const [newUserQuota, setNewUserQuota] = useState({
    userId: '',
    quotaType: '',
    maxValue: '',
    description: ''
  });

  // Состояния для группового управления квотами
  const [isBulkRoleDialogOpen, setIsBulkRoleDialogOpen] = useState(false);
  const [isBulkUserDialogOpen, setIsBulkUserDialogOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkQuotaSettings, setBulkQuotaSettings] = useState({
    quotaType: '',
    maxValue: '',
    description: ''
  });

  // Загрузка данных
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => adminApi.getAllUsers(),
  });

  const { data: roleQuotas = [] } = useQuery<RoleQuota[]>({
    queryKey: ['admin', 'role-quotas'],
    queryFn: () => quotaApi.roleQuotas.list(),
  });

  const { data: userQuotas = [] } = useQuery<UserQuota[]>({
    queryKey: ['admin', 'user-quotas'],
    queryFn: () => quotaApi.userQuotas.list(),
  });

  // Мутации для создания квот
  const createRoleQuotaMutation = useMutation({
    mutationFn: async (data: RoleQuotaCreate) => {
      return await quotaApi.roleQuotas.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'role-quotas'] });
      setNewRoleQuota({ roleId: '', quotaType: '', maxValue: '', description: '' });
      toast({
        title: "Успешно",
        description: "Квота роли создана",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  const createUserQuotaMutation = useMutation({
    mutationFn: async (data: UserQuotaCreate) => {
      return await quotaApi.userQuotas.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-quotas'] });
      setNewUserQuota({ userId: '', quotaType: '', maxValue: '', description: '' });
      toast({
        title: "Успешно",
        description: "Квота пользователя создана",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Мутации для удаления квот
  const deleteRoleQuotaMutation = useMutation({
    mutationFn: async (id: number) => {
      await quotaApi.roleQuotas.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'role-quotas'] });
      toast({
        title: "Успешно",
        description: "Квота роли удалена",
      });
    }
  });

  const deleteUserQuotaMutation = useMutation({
    mutationFn: async (id: number) => {
      await quotaApi.userQuotas.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-quotas'] });
      toast({
        title: "Успешно",
        description: "Квота пользователя удалена",
      });
    }
  });

  // Мутации для группового управления квотами
  const bulkSetRoleQuotasMutation = useMutation({
    mutationFn: async (data: BulkRoleQuotaCreate) => {
      return await quotaApi.roleQuotas.bulkCreate(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'role-quotas'] });
      setIsBulkRoleDialogOpen(false);
      setSelectedRoles([]);
      setBulkQuotaSettings({ quotaType: '', maxValue: '', description: '' });
      toast({
        title: "Успешно",
        description: `Квоты назначены для ${selectedRoles.length} ролей`
      });
    }
  });

  const bulkSetUserQuotasMutation = useMutation({
    mutationFn: async (data: BulkUserQuotaCreate) => {
      return await quotaApi.userQuotas.bulkCreate(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'user-quotas'] });
      setIsBulkUserDialogOpen(false);
      setSelectedUsers([]);
      setBulkQuotaSettings({ quotaType: '', maxValue: '', description: '' });
      toast({
        title: "Успешно",
        description: `Квоты назначены для ${selectedUsers.length} пользователей`
      });
    }
  });

  const handleCreateRoleQuota = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleQuota.roleId || !newRoleQuota.quotaType || !newRoleQuota.maxValue) {
      toast({
        title: "Ошибка",
        description: "Заполните все обязательные поля",
        variant: "destructive",
      });
      return;
    }

    const data: RoleQuotaCreate = {
      role_id: parseInt(newRoleQuota.roleId),
      quota_type: newRoleQuota.quotaType,
      max_value: parseInt(newRoleQuota.maxValue),
      description: newRoleQuota.description || undefined,
    };
    createRoleQuotaMutation.mutate(data);
  };

  const handleCreateUserQuota = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserQuota.userId || !newUserQuota.quotaType || !newUserQuota.maxValue) {
      toast({
        title: "Ошибка",
        description: "Заполните все обязательные поля",
        variant: "destructive",
      });
      return;
    }

    const data: UserQuotaCreate = {
      user_id: newUserQuota.userId,
      quota_type: newUserQuota.quotaType,
      max_value: parseInt(newUserQuota.maxValue),
      description: newUserQuota.description || undefined,
    };
    createUserQuotaMutation.mutate(data);
  };

  const getQuotaTypeLabel = (type: string) => {
    return QUOTA_TYPES.find(qt => qt.value === type)?.label || type;
  };

  const getRoleName = (roleId: number) => {
    return roles.find(r => r.id === roleId)?.name || `Роль ${roleId}`;
  };

  const getUserName = (userId: string) => {
    return users.find(u => u.id === userId)?.username || userId;
  };

  // Функции для группового управления квотами
  const handleBulkRoleQuotas = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkQuotaSettings.quotaType || !bulkQuotaSettings.maxValue || selectedRoles.length === 0) {
      toast({
        title: "Ошибка",
        description: "Выберите роли и заполните все обязательные поля",
        variant: "destructive",
      });
      return;
    }

    const data: BulkRoleQuotaCreate = {
      role_ids: selectedRoles,
      quota_type: bulkQuotaSettings.quotaType,
      max_value: parseInt(bulkQuotaSettings.maxValue),
      description: bulkQuotaSettings.description || undefined,
    };
    bulkSetRoleQuotasMutation.mutate(data);
  };

  const handleBulkUserQuotas = (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkQuotaSettings.quotaType || !bulkQuotaSettings.maxValue || selectedUsers.length === 0) {
      toast({
        title: "Ошибка",
        description: "Выберите пользователей и заполните все обязательные поля",
        variant: "destructive",
      });
      return;
    }

    const data: BulkUserQuotaCreate = {
      user_ids: selectedUsers,
      quota_type: bulkQuotaSettings.quotaType,
      max_value: parseInt(bulkQuotaSettings.maxValue),
      description: bulkQuotaSettings.description || undefined,
    };
    bulkSetUserQuotasMutation.mutate(data);
  };

  const toggleRoleSelection = (roleId: number) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-muted-foreground">
          Настройка лимитов для ролей и пользователей
        </p>
      </div>

      <Tabs defaultValue="role-quotas" className="space-y-4">
        <TabsList>
          <TabsTrigger value="role-quotas">Квоты ролей</TabsTrigger>
          <TabsTrigger value="user-quotas">Квоты пользователей</TabsTrigger>
        </TabsList>

        <TabsContent value="role-quotas" className="space-y-4">
          {/* Кнопки управления */}
          <div className="flex gap-2 mb-4">
            <Dialog open={isBulkRoleDialogOpen} onOpenChange={setIsBulkRoleDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Групповое назначение
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>

          {/* Форма создания квоты роли */}
          <Card>
            <CardHeader>
              <CardTitle>Создать квоту роли</CardTitle>
              <CardDescription>
                Установите лимиты для роли. -1 означает неограниченно.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateRoleQuota} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role-select">Роль</Label>
                    <Select value={newRoleQuota.roleId} onValueChange={(value) => setNewRoleQuota(prev => ({ ...prev, roleId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите роль" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id.toString()}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quota-type-select">Тип квоты</Label>
                    <Select value={newRoleQuota.quotaType} onValueChange={(value) => setNewRoleQuota(prev => ({ ...prev, quotaType: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите тип" />
                      </SelectTrigger>
                      <SelectContent>
                        {QUOTA_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="max-value">Максимальное значение</Label>
                    <Input
                      id="max-value"
                      type="number"
                      value={newRoleQuota.maxValue}
                      onChange={(e) => setNewRoleQuota(prev => ({ ...prev, maxValue: e.target.value }))}
                      placeholder="-1 для неограниченно"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Описание</Label>
                    <Input
                      id="description"
                      value={newRoleQuota.description}
                      onChange={(e) => setNewRoleQuota(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Необязательное описание"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={createRoleQuotaMutation.isPending}>
                  {createRoleQuotaMutation.isPending ? "Создание..." : "Создать квоту"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Список квот ролей */}
          <Card>
            <CardHeader>
              <CardTitle>Существующие квоты ролей</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {roleQuotas.length === 0 ? (
                  <p className="text-muted-foreground">Квоты ролей не настроены</p>
                ) : (
                  roleQuotas.map((quota) => (
                    <div key={quota.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{getRoleName(quota.role_id)}</Badge>
                          <Badge>{getQuotaTypeLabel(quota.quota_type)}</Badge>
                          <span className="font-medium">
                            {quota.max_value === -1 ? "Неограниченно" : quota.max_value}
                          </span>
                        </div>
                        {quota.description && (
                          <p className="text-sm text-muted-foreground mt-1">{quota.description}</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteRoleQuotaMutation.mutate(quota.id)}
                        disabled={deleteRoleQuotaMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="user-quotas" className="space-y-4">
          {/* Кнопки управления */}
          <div className="flex gap-2 mb-4">
            <Dialog open={isBulkUserDialogOpen} onOpenChange={setIsBulkUserDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Групповое назначение
                </Button>
              </DialogTrigger>
            </Dialog>
          </div>

          {/* Форма создания квоты пользователя */}
          <Card>
            <CardHeader>
              <CardTitle>Создать квоту пользователя</CardTitle>
              <CardDescription>
                Установите индивидуальные лимиты для пользователя. Переопределяют квоты ролей.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUserQuota} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="user-select">Пользователь</Label>
                    <Select value={newUserQuota.userId} onValueChange={(value) => setNewUserQuota(prev => ({ ...prev, userId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите пользователя" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.username} ({user.email || user.id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quota-type-select-user">Тип квоты</Label>
                    <Select value={newUserQuota.quotaType} onValueChange={(value) => setNewUserQuota(prev => ({ ...prev, quotaType: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите тип" />
                      </SelectTrigger>
                      <SelectContent>
                        {QUOTA_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="max-value-user">Максимальное значение</Label>
                    <Input
                      id="max-value-user"
                      type="number"
                      value={newUserQuota.maxValue}
                      onChange={(e) => setNewUserQuota(prev => ({ ...prev, maxValue: e.target.value }))}
                      placeholder="-1 для неограниченно"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description-user">Описание</Label>
                    <Input
                      id="description-user"
                      value={newUserQuota.description}
                      onChange={(e) => setNewUserQuota(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Необязательное описание"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={createUserQuotaMutation.isPending}>
                  {createUserQuotaMutation.isPending ? "Создание..." : "Создать квоту"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Список квот пользователей */}
          <Card>
            <CardHeader>
              <CardTitle>Существующие квоты пользователей</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {userQuotas.length === 0 ? (
                  <p className="text-muted-foreground">Квоты пользователей не настроены</p>
                ) : (
                  userQuotas.map((quota) => (
                    <div key={quota.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{getUserName(quota.user_id)}</Badge>
                          <Badge>{getQuotaTypeLabel(quota.quota_type)}</Badge>
                          <span className="font-medium">
                            {quota.max_value === -1 ? "Неограниченно" : quota.max_value}
                          </span>
                        </div>
                        {quota.description && (
                          <p className="text-sm text-muted-foreground mt-1">{quota.description}</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteUserQuotaMutation.mutate(quota.id)}
                        disabled={deleteUserQuotaMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Диалог группового назначения квот ролям */}
      <Dialog open={isBulkRoleDialogOpen} onOpenChange={setIsBulkRoleDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Групповое назначение квот ролям</DialogTitle>
            <DialogDescription>
              Выберите роли и установите квоты для всех выбранных ролей одновременно
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Выбор ролей */}
            <div>
              <Label className="text-base font-medium">Выберите роли:</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                {roles.map((role) => (
                  <div key={role.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role.id}`}
                      checked={selectedRoles.includes(role.id)}
                      onCheckedChange={() => toggleRoleSelection(role.id)}
                    />
                    <Label htmlFor={`role-${role.id}`} className="text-sm">
                      {role.name}
                    </Label>
                  </div>
                ))}
              </div>
              {selectedRoles.length > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Выбрано ролей: {selectedRoles.length}
                </p>
              )}
            </div>

            {/* Настройки квот */}
            <form onSubmit={handleBulkRoleQuotas} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Тип квоты</Label>
                  <Select value={bulkQuotaSettings.quotaType} onValueChange={(value) => setBulkQuotaSettings(prev => ({ ...prev, quotaType: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите тип" />
                    </SelectTrigger>
                    <SelectContent>
                      {QUOTA_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Максимальное значение</Label>
                  <Input
                    type="number"
                    value={bulkQuotaSettings.maxValue}
                    onChange={(e) => setBulkQuotaSettings(prev => ({ ...prev, maxValue: e.target.value }))}
                    placeholder="Введите лимит (-1 для неограниченно)"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Описание (необязательно)</Label>
                <Input
                  value={bulkQuotaSettings.description}
                  onChange={(e) => setBulkQuotaSettings(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Описание квоты"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsBulkRoleDialogOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={bulkSetRoleQuotasMutation.isPending}>
                  {bulkSetRoleQuotasMutation.isPending ? "Назначение..." : "Назначить квоты"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Диалог группового назначения квот пользователям */}
      <Dialog open={isBulkUserDialogOpen} onOpenChange={setIsBulkUserDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Групповое назначение квот пользователям</DialogTitle>
            <DialogDescription>
              Выберите пользователей и установите квоты для всех выбранных пользователей одновременно
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Выбор пользователей */}
            <div>
              <Label className="text-base font-medium">Выберите пользователей:</Label>
              <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onCheckedChange={() => toggleUserSelection(user.id)}
                    />
                    <Label htmlFor={`user-${user.id}`} className="text-sm">
                      {user.username} ({user.email})
                    </Label>
                  </div>
                ))}
              </div>
              {selectedUsers.length > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  Выбрано пользователей: {selectedUsers.length}
                </p>
              )}
            </div>

            {/* Настройки квот */}
            <form onSubmit={handleBulkUserQuotas} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Тип квоты</Label>
                  <Select value={bulkQuotaSettings.quotaType} onValueChange={(value) => setBulkQuotaSettings(prev => ({ ...prev, quotaType: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Выберите тип" />
                    </SelectTrigger>
                    <SelectContent>
                      {QUOTA_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Максимальное значение</Label>
                  <Input
                    type="number"
                    value={bulkQuotaSettings.maxValue}
                    onChange={(e) => setBulkQuotaSettings(prev => ({ ...prev, maxValue: e.target.value }))}
                    placeholder="Введите лимит (-1 для неограниченно)"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Описание (необязательно)</Label>
                <Input
                  value={bulkQuotaSettings.description}
                  onChange={(e) => setBulkQuotaSettings(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Описание квоты"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsBulkUserDialogOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={bulkSetUserQuotasMutation.isPending}>
                  {bulkSetUserQuotasMutation.isPending ? "Назначение..." : "Назначить квоты"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}